/**
 * The MEMBER-facing DEFINITION RPC definitions (OL-114, OL-119).
 *
 * A **loop** is a LIVING definition (`orcaloops.loops`) — refined between
 * invocations, never rewritten in place: every content change appends a frozen
 * `loop_versions` row. This group owns that head; the sibling `loops.rpc.ts`
 * group owns one execution of it.
 *
 * **Defining and refining cost nothing.** Nothing in this group dispatches a
 * workflow or spends a dollar, which is exactly what lets a person sharpen a
 * statement between runs without paying for an execution to find out whether it
 * reads better. `loops.update` is therefore a FIRST-CLASS verb here, not a
 * correction path: the normal life of a loop is define → invoke → refine →
 * invoke again.
 *
 * **Refining never rewrites history.** An invocation carries its own *rendered*
 * `statement`, `acceptance`, `repo`, `ref` and `budgetUsd` on the
 * `orcaloops.outcomes` row and pins `loop_version`, so an update changes what
 * the NEXT invocation renders and nothing about the ones already run — input
 * schema and defaults included, which the rendered statement alone does not
 * preserve.
 *
 * **Rollback is forward.** Re-applying an old version's content is an ordinary
 * `loops.update` that produces version *n+1*; the head pointer never moves
 * backward, so history stays linear. There is deliberately **no `revert` /
 * `restore` rpc**, and no write here may lower or reuse a `version` value.
 *
 * **Archive, never delete.** There is no delete rpc in this group and none may
 * be added: an invocation's `loop_id` must keep resolving for as long as the
 * invocation exists, which is forever.
 *
 * **A loop may carry a `body`.** Beside its templates a definition may hold a v3
 * loop document (`@fm/db/models/LoopDocument`) — the graph, agent or
 * deterministic action the loop runs. Defining or refining one costs nothing,
 * exactly as a template edit does. A `body` is CONTENT, so changing it (setting,
 * refining or clearing it to `null`) appends version *n+1* through
 * `patchTouchesContent`, and rolling a document back is a forward `loops.update`
 * carrying the earlier document — there is still no `revert`. Every `body` is
 * gated by {@link validateLoopBody} BEFORE the write, so a refusal leaves neither
 * a head nor a version row behind and names the rule that refused it.
 *
 * **`customerId` on a payload here is a SELECTOR among the caller's own
 * memberships, never an authority.** `resolveActorCustomer` (`loop-scope.ts`) is
 * the only thing that turns a request into a customer scope.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Loop, LoopInput } from "@fm/db/models/Loop";
import type { LoopDocumentValidator } from "@fm/db/models/LoopDocument";
import {
  composeLoopDocumentValidators,
  formatLoopDocumentRefusal,
  LoopDocument,
  structuralLoopDocumentValidator,
  validateLoopDocumentForAppend,
} from "@fm/db/models/LoopDocument";
import { LoopVersion } from "@fm/db/models/LoopVersion";
import { OutcomeStatus } from "@fm/db/models/Outcome";
import { Trigger } from "@fm/db/models/Trigger";
import type { LoopDocument as GrammarLoopDocument } from "@fm/loops/document/schema";
import { Either, Schema } from "effect";

import { templateInputNames } from "../../services/orcaloops/render-invocation.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized, ValidationError } from "../errors.ts";
import { LoopActorMiddleware } from "../loop-actor.tags.ts";
import { makeLoopGrammarValidator } from "./loop-grammar-validator.ts";
import { LoopInvocationSummary } from "./loop-viewer.rpc.ts";

/**
 * One loop definition addressed by id, scoped to the caller's resolved customer.
 *
 * @since 1.0.0
 */
export const LoopRefInput = Schema.Struct({
  loopId: Schema.String,
  customerId: Schema.optional(Schema.String),
});
export type LoopRefInput = Schema.Schema.Type<typeof LoopRefInput>;

/**
 * How much of an invocation's statement a compact row is allowed to carry.
 *
 * ONE constant, read by this group and by the invocation group, so the two
 * cannot clamp differently.
 *
 * @since 1.0.0
 */
export const STATEMENT_PREVIEW_MAX = 120;

/**
 * One invocation as a list renders it. NEVER carries the statement body.
 *
 * Declared HERE and imported by the invocation group, so there is one
 * compact-invocation row in the codebase rather than two that drift. Engine
 * naming (`id`, not `invocationId`); the MCP layer renames.
 *
 * It carries no run rows at all — the simplest way to honour the rule that
 * `orcaloops.runs.deferred_token` and `callback_token_hash` never leave the api.
 *
 * @since 1.0.0
 */
export const LoopInvocationRow = Schema.Struct({
  id: Schema.String,
  loopId: Schema.NullOr(Schema.String),
  /** WHICH version of that living definition this execution rendered from; null = ad hoc. */
  loopVersion: Schema.NullOr(Schema.Int),
  inputs: Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.String })),
  title: Schema.String,
  /** The statement clamped to 120 characters — NEVER the statement body. */
  statementPreview: Schema.String,
  status: OutcomeStatus,
  repo: Schema.NullOr(Schema.String),
  ref: Schema.NullOr(Schema.String),
  budgetUsd: Schema.NullOr(Schema.Number),
  spentUsd: Schema.Number,
  turn: Schema.Number,
  maxTurns: Schema.Number,
  triggers: Schema.Array(Trigger.json),
  lastReportAt: Schema.NullOr(Schema.DateTimeUtc),
  runCounts: Schema.Struct({ running: Schema.Number, waiting: Schema.Number, failed: Schema.Number }),
  createdAt: Schema.DateTimeUtc,
  updatedAt: Schema.DateTimeUtc,
});
export type LoopInvocationRow = Schema.Schema.Type<typeof LoopInvocationRow>;

/**
 * A new definition, as a person (or their agent) files it.
 *
 * `inputSchema` is `Schema.Array(LoopInput)` over the ONE input-element symbol
 * in the project — the extractor's draft, this payload and the `input_schema`
 * column all decode the same struct, so a draft's `inputSchema` is
 * byte-for-byte acceptable here without reshaping. `description` and `example`
 * are `Schema.NullOr` on that struct, never optional, so an entry carrying
 * `description: null` is valid input and must not be rejected.
 *
 * @since 1.0.0
 */
export const LoopDefineInput = Schema.Struct({
  name: Schema.String,
  slug: Schema.String,
  description: Schema.optional(Schema.NullOr(Schema.String)),
  statementTemplate: Schema.String,
  acceptanceTemplate: Schema.optional(Schema.NullOr(Schema.String)),
  inputSchema: Schema.Array(LoopInput),
  defaultBudgetUsd: Schema.optional(Schema.NullOr(Schema.Number)),
  defaultMaxParallel: Schema.optional(Schema.NullOr(Schema.Int)),
  defaultMaxTurns: Schema.optional(Schema.NullOr(Schema.Int)),
  defaultRepo: Schema.optional(Schema.NullOr(Schema.String)),
  defaultRef: Schema.optional(Schema.NullOr(Schema.String)),
  body: Schema.optional(Schema.NullOr(LoopDocument)),
  customerId: Schema.optional(Schema.String),
});
export type LoopDefineInput = Schema.Schema.Type<typeof LoopDefineInput>;

/**
 * The filed definition, and whether THIS call is what filed it.
 *
 * `created: false` is the idempotent answer to a retried define: the existing
 * head comes back untouched, no second version row is appended, and no unique
 * violation is raised. Changing a definition is `loops.update`, never a second
 * `define`.
 *
 * @since 1.0.0
 */
export const LoopDefineResponse = Schema.Struct({
  loop: Loop.json,
  created: Schema.Boolean,
});
export type LoopDefineResponse = Schema.Schema.Type<typeof LoopDefineResponse>;

/**
 * A page of the caller's definitions.
 *
 * @since 1.0.0
 */
export const LoopListInput = Schema.Struct({
  includeArchived: Schema.optional(Schema.Boolean),
  limit: Schema.optional(Schema.Int),
  cursor: Schema.optional(Schema.String),
  customerId: Schema.optional(Schema.String),
});
export type LoopListInput = Schema.Schema.Type<typeof LoopListInput>;

/**
 * One definition as the list renders it — deliberately bounded.
 *
 * There is **no `statementTemplate` and no `acceptanceTemplate`**: a template is
 * unbounded text and is the business of `loops.get` and the resource, not of a
 * row. `inputs` carries the input schema MINUS each entry's `description`, for
 * the same reason.
 *
 * `version` says which version is live, so the MCP `loops_list` tool and the
 * e2e walk can read it without a second call.
 *
 * @since 1.0.0
 */
export const LoopListRow = Schema.Struct({
  loopId: Schema.String,
  name: Schema.String,
  slug: Schema.String,
  description: Schema.NullOr(Schema.String),
  version: Schema.Int,
  inputs: Schema.Array(
    Schema.Struct({
      name: Schema.String,
      required: Schema.Boolean,
      example: Schema.NullOr(Schema.String),
    }),
  ),
  defaultBudgetUsd: Schema.NullOr(Schema.Number),
  defaultMaxParallel: Schema.NullOr(Schema.Number),
  defaultMaxTurns: Schema.NullOr(Schema.Number),
  defaultRepo: Schema.NullOr(Schema.String),
  defaultRef: Schema.NullOr(Schema.String),
  invocationCount: Schema.Number,
  lastInvokedAt: Schema.NullOr(Schema.DateTimeUtc),
  archivedAt: Schema.NullOr(Schema.DateTimeUtc),
});
export type LoopListRow = Schema.Schema.Type<typeof LoopListRow>;

/**
 * A keyset page: the rows, the cursor that resumes after them, and whether
 * resuming would find anything.
 *
 * @since 1.0.0
 */
export const LoopListResponse = Schema.Struct({
  loops: Schema.Array(LoopListRow),
  cursor: Schema.NullOr(Schema.String),
  hasMore: Schema.Boolean,
});
export type LoopListResponse = Schema.Schema.Type<typeof LoopListResponse>;

/**
 * One entry of a loop's history, as the detail view lists it.
 *
 * Deliberately NO content: a version's templates are `loops.getVersion` and the
 * resource, for the same unbounded-text reason {@link LoopListRow} carries no
 * templates.
 *
 * @since 1.0.0
 */
export const LoopVersionSummary = Schema.Struct({
  version: Schema.Int,
  createdAt: Schema.DateTimeUtc,
  changeNote: Schema.NullOr(Schema.String),
});
export type LoopVersionSummary = Schema.Schema.Type<typeof LoopVersionSummary>;

/**
 * The head, its ten most recent editions, and its ten most recent executions.
 *
 * Executions come in two id spaces: `recentInvocations` is the v1
 * `orcaloops.outcomes` list, and `recentLoopInvocations` is the v2
 * `orcaloops.loop_invocations` list — every Loop Studio / harness run — newest
 * first, as the viewer's Invocations tab renders it (secret-free
 * `LoopInvocationSummary` rows).
 *
 * Still served for an ARCHIVED definition: an invocation's `loop_id` must keep
 * resolving.
 *
 * @since 1.0.0
 */
export const LoopDetailResponse = Schema.Struct({
  loop: Loop.json,
  versions: Schema.Array(LoopVersionSummary),
  recentInvocations: Schema.Array(LoopInvocationRow),
  recentLoopInvocations: Schema.Array(LoopInvocationSummary),
});
export type LoopDetailResponse = Schema.Schema.Type<typeof LoopDetailResponse>;

/**
 * One frozen edition of one definition, addressed by (loop, version number).
 *
 * @since 1.0.0
 */
export const LoopVersionRefInput = Schema.Struct({
  loopId: Schema.String,
  version: Schema.Int,
  customerId: Schema.optional(Schema.String),
});
export type LoopVersionRefInput = Schema.Schema.Type<typeof LoopVersionRefInput>;

/**
 * One frozen edition PLUS enough of the head to say what the loop is now.
 *
 * `headVersion` is here so a reader rendering a pinned version can say "the head
 * is now version *m*" without a second read — the `orcaloops://loops/<id>/versions/<n>`
 * resource renders exactly this struct and reads `headVersion` off it.
 *
 * `loop` is the head's IDENTITY only, never its content: the content on this
 * response is the frozen row's, and mixing the two would be the exact confusion
 * versions exist to prevent.
 *
 * @since 1.0.0
 */
export const LoopVersionResponse = Schema.Struct({
  /** The immutable row: content, changeNote, createdAt, createdByPersonId. */
  version: LoopVersion.json,
  /** The head's CURRENT version — lets a reader say "the head is now version m". */
  headVersion: Schema.Int,
  /** The head's identity only — never its content. */
  loop: Schema.Struct({
    id: Schema.String,
    name: Schema.String,
    slug: Schema.String,
    description: Schema.NullOr(Schema.String),
  }),
});
export type LoopVersionResponse = Schema.Schema.Type<typeof LoopVersionResponse>;

/**
 * A PARTIAL patch over every editable field of a definition, so a person can
 * sharpen one sentence without re-sending the loop.
 *
 * `slug` is deliberately absent and is not editable: it is the handle people
 * type and the idempotency key `define` collides on, so renaming it would break
 * both.
 *
 * `name` and `description` are IDENTITY — patching only those appends no
 * version row. Any of the nine content fields — `body` included — makes this a
 * versioned update. The routing is `patchTouchesContent` from
 * `@fm/db/models/Loop`; this module declares no identity/content field list of
 * its own.
 *
 * @since 1.0.0
 */
export const LoopPatchInput = Schema.Struct({
  name: Schema.optional(Schema.String),
  description: Schema.optional(Schema.NullOr(Schema.String)),
  statementTemplate: Schema.optional(Schema.String),
  acceptanceTemplate: Schema.optional(Schema.NullOr(Schema.String)),
  inputSchema: Schema.optional(Schema.Array(LoopInput)),
  defaultBudgetUsd: Schema.optional(Schema.NullOr(Schema.Number)),
  defaultMaxParallel: Schema.optional(Schema.NullOr(Schema.Int)),
  defaultMaxTurns: Schema.optional(Schema.NullOr(Schema.Int)),
  defaultRepo: Schema.optional(Schema.NullOr(Schema.String)),
  defaultRef: Schema.optional(Schema.NullOr(Schema.String)),
  body: Schema.optional(Schema.NullOr(LoopDocument)),
});
export type LoopPatchInput = Schema.Schema.Type<typeof LoopPatchInput>;

/**
 * The refine call. `changeNote` lands on the appended version row ONLY — the
 * head carries no such column, because the note explains an edition, not a
 * definition.
 *
 * @since 1.0.0
 */
export const LoopUpdateInput = Schema.Struct({
  loopId: Schema.String,
  patch: LoopPatchInput,
  changeNote: Schema.optional(Schema.NullOr(Schema.String)),
  customerId: Schema.optional(Schema.String),
});
export type LoopUpdateInput = Schema.Schema.Type<typeof LoopUpdateInput>;

/**
 * The content a template check needs — the shape both `define` (from its
 * payload) and `update` (from the patch MERGED onto the stored head) reduce to.
 *
 * @since 1.0.0
 */
export interface LoopTemplateContent {
  readonly slug: string;
  readonly name: string;
  readonly statementTemplate: string;
  readonly acceptanceTemplate: string | null;
  readonly inputSchema: ReadonlyArray<LoopInput>;
}

/**
 * Do the templates and the input schema agree, in BOTH directions?
 *
 * Every `{{name}}` in `name` / `statementTemplate` / `acceptanceTemplate` must
 * have an `inputSchema` entry, and every `inputSchema` entry must occur in at
 * least one of them. A declared-but-unused input and an undeclared-but-
 * referenced one are both typos, and a typo caught here is a definition never
 * written — as opposed to a definition every invocation of would refuse at
 * render time, which the person only discovers after the fact.
 *
 * `loops.update` runs this over the MERGED row, so a loop can never be edited
 * into a state a fresh `define` would have refused.
 *
 * Pure — no database, no layer.
 *
 * @since 1.0.0
 */
export const validateTemplateInputs = (
  content: LoopTemplateContent,
): Either.Either<void, ValidationError> => {
  const referenced = new Set<string>([
    ...templateInputNames(content.name),
    ...templateInputNames(content.statementTemplate),
    ...templateInputNames(content.acceptanceTemplate ?? ""),
  ]);

  const declared = new Set(content.inputSchema.map((entry) => entry.name));
  const undeclared = [...referenced].find((name) => !declared.has(name));
  if (undeclared !== undefined) {
    return Either.left(
      new ValidationError({
        field: "statementTemplate",
        message: `loop ${content.slug} template references undeclared input ${undeclared}`,
      }),
    );
  }

  const unused = content.inputSchema.find((entry) => !referenced.has(entry.name));
  return unused === undefined
    ? Either.right(undefined)
    : Either.left(
      new ValidationError({
        field: "inputSchema",
        message: `loop ${content.slug} declares input ${unused.name} that no template references`,
      }),
    );
};

/**
 * The ONE validator a `body` passes through, named through a single factory so
 * the swap surface is one line.
 *
 * Two validators run, in order. `@fm/db`'s three STRUCTURAL rules go first — a
 * body is a JSON object, survives a jsonb round-trip, and is not unbounded —
 * because they are the only rules that can be defended on a column's own
 * evidence, and because `composeLoopDocumentValidators` short-circuits on the
 * first refusal, so an oversized blob is refused as oversized rather than as
 * unparseable. `@fm/loops`' GRAMMAR validator runs second over the document that
 * survived them.
 *
 * It is a factory rather than a constant because the grammar validator is bound
 * to a `resolvable` set — the calling customer's OTHER loop documents, which is
 * what `outcome_ref_resolves`, `outcome_contract` and `version_resolves` need in
 * order to resolve a `@version` reference. Do not scatter the reference across
 * call sites — {@link validateLoopBody} is the only reader, and that is
 * deliberate.
 *
 * @since 1.0.0
 */
export const loopDocumentValidatorFor = (
  resolvable: ReadonlyArray<GrammarLoopDocument>,
): LoopDocumentValidator =>
  composeLoopDocumentValidators(
    structuralLoopDocumentValidator,
    makeLoopGrammarValidator(resolvable),
  );

/**
 * Does the loop `body` this write carries survive its gate?
 *
 * An absent body ("leave the document alone") and an explicit `null` ("clear the
 * document") both pass untouched — only a present document is validated. A
 * refusal comes back as a `ValidationError` on `field: "body"` whose message
 * NAMES the refusing rule, so a person handing an agent a graph that does not
 * validate is told which rule refused it and can repair the document rather than
 * guess. The rule-id spelling lives once, in {@link formatLoopDocumentRefusal},
 * and the message is prefixed with the slug the way {@link validateTemplateInputs}
 * prefixes its refusals.
 *
 * `loops.update` runs this over the MERGED row, so a loop can never be edited
 * into a state a fresh `define` would have refused.
 *
 * `resolvable` is the set of OTHER loop documents a reference in this body may
 * resolve against — always the calling customer's own, loaded by
 * `loadResolvableDocuments` (`loop-grammar-validator.ts`) from the id the router
 * resolved for the caller. The caller loads it; this function stays pure — no
 * database, no layer.
 *
 * @since 1.0.0
 */
export const validateLoopBody = (
  slug: string,
  body: LoopDocument | null | undefined,
  resolvable: ReadonlyArray<GrammarLoopDocument>,
): Either.Either<void, ValidationError> =>
  validateLoopDocumentForAppend(loopDocumentValidatorFor(resolvable), body).pipe(
    Either.mapLeft((refusal) =>
      new ValidationError({
        field: "body",
        message: `loop ${slug} ${formatLoopDocumentRefusal(refusal)}`,
      })
    ),
    Either.map(() => undefined),
  );

/**
 * The member-facing definition RPC group. Every member carries
 * {@link LoopActorMiddleware}, and every payload's `customerId` is optional and
 * is a selector, never an authority.
 *
 * The wire row is `Loop.json` from `@fm/db/models/Loop` — never redeclared here.
 *
 * @since 1.0.0
 */
export class LoopDefinitionRpcs extends RpcGroup.make(
  Rpc.make("loops.define", {
    payload: LoopDefineInput,
    success: LoopDefineResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.list", {
    payload: LoopListInput,
    success: LoopListResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.get", {
    payload: LoopRefInput,
    success: LoopDetailResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.getVersion", {
    payload: LoopVersionRefInput,
    success: LoopVersionResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.update", {
    payload: LoopUpdateInput,
    success: Loop.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.archive", {
    payload: LoopRefInput,
    success: Loop.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
) {}
